import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Button, ChipToggle, Dialog, Field, Input } from '@/components/ui';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import type { Bill, PaymentInput } from '@/features/billing/types';
import { BillingLimitBanner } from '@/features/limit-billing/BillingLimitBanner';
import type {
  LimitAssessmentConfirmation,
  LimitBillingAssessment,
} from '@/features/limit-billing/api';
import styles from '@/pages/queue/queue.module.css';

type Method = 'cash' | 'card' | 'e_wallet' | 'duitnow_qr';

const METHODS: { value: Method; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'e_wallet', label: 'E-wallet' },
  { value: 'duitnow_qr', label: 'DuitNow QR' },
];

/** The DuitNow QR shown to the patient (artifact #31). */
export function DuitNowQr({ payload }: { payload: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(payload, { margin: 1, width: 168 })
      .then((d) => live && setSrc(d))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [payload]);
  return src ? (
    <img src={src} width={168} height={168} alt="DuitNow QR" />
  ) : null;
}

/**
 * "Take payment": cash (tendered → change), card or e-wallet (approval code),
 * or DuitNow QR, which is paid by the bank's confirmation (never by hand).
 * Paying part of the balance leaves the rest for a second method (split).
 */
export function PaymentDialog({
  open,
  bill,
  busy,
  onClose,
  onPay,
  onDuitNow,
  assessment,
  assessmentLoading,
  assessmentError,
  assessmentConfirmed,
  onAssessmentConfirmedChange,
  onRetryAssessment,
  paymentError,
}: {
  open: boolean;
  bill: Bill;
  busy: boolean;
  onClose: () => void;
  onPay: (input: PaymentInput) => void;
  onDuitNow: (
    amountSen: number,
    limitAssessment?: LimitAssessmentConfirmation,
  ) => void;
  assessment?: LimitBillingAssessment;
  assessmentLoading: boolean;
  assessmentError?: unknown;
  assessmentConfirmed: boolean;
  onAssessmentConfirmedChange: (confirmed: boolean) => void;
  onRetryAssessment: () => void;
  paymentError?: unknown;
}) {
  const [method, setMethod] = useState<Method>('cash');
  const [amount, setAmount] = useState(senToInput(bill.balanceSen));
  const [tendered, setTendered] = useState('');
  const [reference, setReference] = useState('');
  const amountSen = parseRM(amount);
  const tenderedSen = tendered.trim() ? parseRM(tendered) : null;
  const amountOk =
    amountSen !== null && amountSen >= 1 && amountSen <= bill.balanceSen;
  const tenderedOk =
    method !== 'cash' ||
    !tendered.trim() ||
    (tenderedSen !== null && amountSen !== null && tenderedSen >= amountSen);
  const change =
    method === 'cash' && tenderedSen !== null && amountSen !== null
      ? tenderedSen - amountSen
      : null;
  const requiresAssessment = !!bill.payer;
  const limitAssessment =
    assessment && assessmentConfirmed
      ? {
          assessmentId: assessment.id,
          confirmed: true as const,
          panelCoverageSen: assessment.split.panelCoverageSen,
          patientPaySen: assessment.split.patientPaySen,
        }
      : undefined;
  const assessmentReady =
    !requiresAssessment ||
    (!!limitAssessment && !assessmentLoading && !assessmentError);

  return (
    <Dialog
      open={open}
      variant="modal"
      title={`Take payment · ${formatRM(bill.balanceSen)} due`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!amountOk || !tenderedOk || !assessmentReady}
            loading={busy}
            onClick={() => {
              if (amountSen === null) return;
              if (method === 'duitnow_qr')
                return onDuitNow(amountSen, limitAssessment);
              onPay({
                version: bill.version,
                method,
                amountSen,
                tenderedSen:
                  method === 'cash' && tenderedSen !== null
                    ? tenderedSen
                    : undefined,
                reference:
                  method !== 'cash' ? reference.trim() || undefined : undefined,
                limitAssessment,
              });
            }}
          >
            {method === 'duitnow_qr' ? 'Show QR' : 'Take payment'}
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        {requiresAssessment && (
          <BillingLimitBanner
            assessment={assessment}
            loading={assessmentLoading}
            error={paymentError ?? assessmentError}
            confirmed={assessmentConfirmed}
            onConfirmedChange={onAssessmentConfirmedChange}
            onRetry={onRetryAssessment}
          />
        )}
        <ChipToggle
          label="Method"
          single
          options={METHODS}
          selected={[method]}
          onChange={(v) => v[0] && setMethod(v[0])}
        />
        <Field
          label="Amount (RM)"
          hint="Less than the balance leaves the rest for a second method."
          error={
            amount && !amountOk
              ? `Up to ${formatRM(bill.balanceSen)}`
              : undefined
          }
        >
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(ev) => setAmount(ev.target.value)}
          />
        </Field>
        {method === 'cash' && (
          <Field
            label="Cash handed over (RM, optional)"
            error={!tenderedOk ? 'Less than the amount' : undefined}
            hint={
              change !== null && change >= 0
                ? `Change ${formatRM(change)}`
                : undefined
            }
          >
            <Input
              inputMode="decimal"
              value={tendered}
              onChange={(ev) => setTendered(ev.target.value)}
            />
          </Field>
        )}
        {(method === 'card' || method === 'e_wallet') && (
          <Field label="Approval code (optional)">
            <Input
              value={reference}
              onChange={(ev) => setReference(ev.target.value)}
            />
          </Field>
        )}
        {method === 'duitnow_qr' && (
          <p className={styles.sub}>
            The QR carries the amount and the bill reference. The bill updates
            by itself when the bank confirms; nobody marks it paid by hand.
          </p>
        )}
      </div>
    </Dialog>
  );
}
